import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchProjectAccess, type ProjectAccess, type ProjectPerson } from './projectAccessApi';

const sameIds = (a: string[], b: string[]) => a.length === b.length && [...a].sort().every((id, i) => id === [...b].sort()[i]);

/**
 * The members of a project as the Edit Project modal edits them: loaded when the modal opens, changed only in local state, and handed back to the modal's own Save Changes.
 * Closing the modal drops the draft (the next open loads the stored list again).
 */
export function useProjectMembers(projectId: string | null | undefined, active: boolean, { forNewProject = false }: { forNewProject?: boolean } = {}) {
  const [access, setAccess] = useState<ProjectAccess | null>(null);
  const [draft, setDraft] = useState<ProjectPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const adopt = (next: ProjectAccess) => {
    setAccess(next);
    setDraft([...next.internal_members, ...next.external_members]);
  };

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError(null);
    try {
      adopt(await fetchProjectAccess(projectId));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Project members could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (active && projectId) void load();
    // a project being created has no stored members yet: the draft starts empty (the server adds the creator); the creator fills it with anybody
    else if (active && forNewProject) { setAccess({ project_id: '', can_manage: true, internal_members: [], external_members: [] }); setDraft([]); setLoadError(null); }
    else { setAccess(null); setDraft([]); setLoadError(null); }
  }, [active, projectId, forNewProject, load]);

  const storedIds = useMemo(() => (access ? [...access.internal_members, ...access.external_members].map((p) => p.local_id) : []), [access]);
  const ids = useMemo(() => draft.map((p) => p.local_id), [draft]);
  const internal = useMemo(() => draft.filter((p) => p.kind === 'internal'), [draft]);
  const external = useMemo(() => draft.filter((p) => p.kind === 'external'), [draft]);

  return {
    loading,
    loadError,
    load,
    canManage: !!access?.can_manage,
    loaded: !!access,
    internal,
    external,
    ids,
    storedIds,
    dirty: !!access && !sameIds(ids, storedIds),
    add: (people: ProjectPerson[]) => setDraft((d) => [...d, ...people.filter((p) => !d.some((x) => x.local_id === p.local_id))]),
    remove: (localId: string) => setDraft((d) => d.filter((p) => p.local_id !== localId)),
  };
}

export type ProjectMembersState = ReturnType<typeof useProjectMembers>;
